import { BaseWidget, WidgetConfig, WidgetConstructor } from './model';

/**
 * Abstract base class for widget factories.
 * Provides shared implementation for registry management methods.
 * Framework-specific factories should extend this class.
 *
 * @typeParam TConstructor - The widget constructor type
 * @typeParam TExtraArgs - Additional framework-specific arguments passed to create() after container
 */
export abstract class BaseWidgetFactory<
  TConstructor extends WidgetConstructor,
  TExtraArgs extends unknown[] = unknown[],
> {
  protected readonly registry = new Map<string, TConstructor>();
  protected readonly presets = new Map<string, WidgetConfig>();

  // ==============================================================================
  // WIDGET TYPE REGISTRY
  // ==============================================================================

  // Register a widget type with the factory.
  register(type: string, widgetClass: TConstructor): void {
    if (this.registry.has(type)) {
      console.warn(`Widget type '${type}' is already registered. Overwriting.`);
    }
    this.registry.set(type, widgetClass);
  }

  /**
   * Create a widget instance of the specified type.
   * Handles registry lookup and error handling; delegates instantiation to subclasses.
   */
  create(type: string, container: HTMLElement, ...args: TExtraArgs): BaseWidget {
    const WidgetClass = this.registry.get(type);

    if (!WidgetClass) {
      throw new Error(
        `Unknown widget type: '${type}'. Available types: ${Array.from(this.registry.keys()).join(', ')}`,
      );
    }

    try {
      return this.instantiate(WidgetClass, container, ...args);
    } catch (error) {
      throw new Error(`Failed to create widget of type '${type}': ${String(error)}`);
    }
  }

  // Check if a widget type is registered.
  has(type: string): boolean {
    return this.registry.has(type);
  }

  // Get all registered widget type names.
  getRegisteredTypes(): string[] {
    return Array.from(this.registry.keys());
  }

  // ==============================================================================
  // PRESET REGISTRY
  // ==============================================================================

  // Register a preset configuration. ID and type are derived from config.
  registerPreset(config: WidgetConfig): void {
    if (this.presets.has(config.id)) {
      console.warn(`Preset '${config.id}' is already registered. Overwriting.`);
    }
    this.presets.set(config.id, config);
  }

  // Get a preset configuration by ID.
  getPreset(presetId: string): WidgetConfig | undefined {
    return this.presets.get(presetId);
  }

  // List all preset IDs, optionally filtered by widget type.
  listPresetIds(type?: string): string[] {
    if (type) {
      return Array.from(this.presets.values())
        .filter((config) => config.type === type)
        .map((config) => config.id);
    }
    return Array.from(this.presets.keys());
  }

  // Get all presets, optionally filtered by widget type.
  listPresets(type?: string): WidgetConfig[] {
    const all = Array.from(this.presets.values());
    return type ? all.filter((config) => config.type === type) : all;
  }

  /**
   * Convenience method to get a preset config.
   * Throws if the preset is not found.
   *
   * @example
   * ```ts
   * const config = factory.widget('linear');
   * ```
   */
  widget(presetId: string): WidgetConfig {
    const preset = this.getPreset(presetId);
    if (!preset) {
      throw new Error(`Unknown preset '${presetId}'. Available presets: ${this.listPresetIds().join(', ') || 'none'}`);
    }
    return preset;
  }

  // ==============================================================================
  // ABSTRACT METHODS
  // ==============================================================================

  // Framework-specific: instantiate the widget with framework-specific dependencies
  protected abstract instantiate(WidgetClass: TConstructor, container: HTMLElement, ...args: TExtraArgs): BaseWidget;

  // Framework-specific: load the framework library
  abstract loadFramework(): Promise<unknown>;
}
